import * as React from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import ColumnLayout from "@amzn/awsui-components-react/polaris/column-layout";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Input from "@amzn/awsui-components-react/polaris/input";
import Textarea from "@amzn/awsui-components-react/polaris/textarea";
import Autosuggest from "@amzn/awsui-components-react/polaris/autosuggest";
import ExpandableSection from "@amzn/awsui-components-react/polaris/expandable-section";
import SegmentedControl from "@amzn/awsui-components-react/polaris/segmented-control";
import { MedicationOption } from "../add_prescription/PrescriptionDetailsBlock";
import { NewRxMedicationFormState, DrugSource, QUANTITY_UOM_OPTIONS } from "./NewRxItemTypes";

interface Props {
  drugSource: DrugSource;
  drugId: string;
  drugIdInput: string;
  medication: NewRxMedicationFormState;
  medications: MedicationOption[];
  onDrugSourceChange: (source: DrugSource) => void;
  onDrugChange: (updates: { drugId: string; drugIdInput: string }) => void;
  onMedicationChange: (updates: Partial<NewRxMedicationFormState>) => void;
}

/**
 * Drug picker + NewRxMedication fields for one NewRxBatchItem.
 *
 * A "Drug source" choice makes the drugId-vs-NDC decision explicit:
 *   - "Drug ID" (default): the drug Autosuggest is shown; the server resolves the drug (and infers the
 *     unit of measure). An optional NDC override may be supplied to pin a specific NDC for the drug.
 *   - "NDC": the drug dropdown is hidden and ndc + quantityUnitOfMeasure become required inputs.
 */
class NewRxMedicationBlock extends React.Component<Props> {
  render() {
    const {
      drugSource,
      drugId,
      drugIdInput,
      medication,
      medications,
      onDrugSourceChange,
      onDrugChange,
      onMedicationChange,
    } = this.props;

    // Once a drug is selected, drugIdInput holds the full display string ("Desc (drugId)"), which
    // matches neither an option's drugId nor its description — filtering would then show nothing and
    // block re-selection. When the input still corresponds to the selected drug, show all options.
    const selectedLabel = (() => {
      const sel = medications.find((m) => m.drugId === drugId);
      return sel ? `${sel.description || sel.drugId} (${sel.drugId})` : "";
    })();
    const inputMatchesSelection = !!drugId && drugIdInput === selectedLabel;

    const filteredOptions = medications
      .filter(
        (m) =>
          !drugIdInput ||
          inputMatchesSelection ||
          m.drugId.toLowerCase().includes(drugIdInput.toLowerCase()) ||
          (m.description || "").toLowerCase().includes(drugIdInput.toLowerCase())
      )
      .map((m) => ({ value: m.drugId, label: `${m.description || m.drugId} (${m.drugId})` }));

    const useNdc = drugSource === "ndc";

    // Quantity-UoM Autosuggest (mirrors the drug search so the input stays in place rather than a
    // Select popover that can flip upward). The field stores the bare NCI code; show the friendly
    // "Label (Code)" when the stored value is a known code, and filter options by typed text.
    const uomOptions = QUANTITY_UOM_OPTIONS.map((o) => ({ value: o.value, label: `${o.label} (${o.value})` }));
    const uomValue = medication.quantityUnitOfMeasure;
    const uomSelected = uomOptions.find((o) => o.value === uomValue);
    const uomInputValue = uomSelected ? uomSelected.label : uomValue;
    // When the input still matches the current selection, show all options so the user can re-pick
    // (same guard as the drug Autosuggest above).
    const uomMatchesSelection = !!uomSelected && uomInputValue === uomSelected.label;
    const uomFilteredOptions = uomOptions.filter(
      (o) =>
        !uomInputValue ||
        uomMatchesSelection ||
        o.label.toLowerCase().includes(uomInputValue.toLowerCase()) ||
        o.value.toLowerCase().includes(uomInputValue.toLowerCase())
    );
    // Keep the stored value a bare NCI code: resolve typed text to a code when it matches an option's
    // code, its plain form name, or the "Label (Code)" display; otherwise store the raw text so validation
    // rejects it.
    const resolveUom = (typed: string): string => {
      const t = typed.trim().toLowerCase();
      const match = QUANTITY_UOM_OPTIONS.find(
        (o) =>
          o.value.toLowerCase() === t ||
          o.label.toLowerCase() === t ||
          `${o.label} (${o.value})`.toLowerCase() === t
      );
      return match ? match.value : typed;
    };
    const uomInvalid = !!uomValue && !uomSelected;

    return (
      <Container header={<Header variant="h3">Drug &amp; Medication</Header>}>
        <SpaceBetween size="m">
          <FormField
            label="Drug Source"
            description="Choose how this prescription's drug is identified."
          >
            <SegmentedControl
              selectedId={drugSource}
              onChange={({ detail }) => onDrugSourceChange(detail.selectedId as DrugSource)}
              options={[
                { id: "drugId", text: "Drug ID" },
                { id: "ndc", text: "NDC" },
              ]}
            />
          </FormField>

          {!useNdc && (
            <FormField
              label="Drug"
              description="Drug name / strength / NDC are resolved from the selected drug."
            >
              <Autosuggest
                value={drugIdInput || drugId || ""}
                onChange={({ detail }) => onDrugChange({ drugIdInput: detail.value, drugId: "" })}
              onSelect={({ detail }) => {
                const med = medications.find((m) => m.drugId === detail.value);
                onDrugChange({
                  drugId: detail.value,
                  drugIdInput: med ? `${med.description || med.drugId} (${detail.value})` : detail.value,
                });
              }}
              options={filteredOptions}
              placeholder="Search medications..."
              empty="No matching medications"
            />
            </FormField>
          )}

          {!useNdc && (
            <FormField
              label="NDC Override (Optional)"
              description="Leave blank to let the server pick the drug's active NDC. Set it to ingest a specific NDC for the selected drug."
            >
              <Input
                value={medication.ndc}
                onChange={({ detail }) => onMedicationChange({ ndc: detail.value })}
                placeholder="Server resolves from the drug if blank"
              />
            </FormField>
          )}

          <ColumnLayout columns={2}>
            <FormField label="Quantity" description="Required">
              <Input
                type="number"
                value={medication.quantity}
                onChange={({ detail }) => onMedicationChange({ quantity: detail.value })}
              />
            </FormField>
            <FormField label="Days Supply" description="Required">
              <Input
                type="number"
                value={medication.daysSupply}
                onChange={({ detail }) => onMedicationChange({ daysSupply: detail.value })}
              />
            </FormField>
            <FormField label="Number of Refills" description="Required">
              <Input
                type="number"
                value={medication.numberOfRefills}
                onChange={({ detail }) => onMedicationChange({ numberOfRefills: detail.value })}
              />
            </FormField>
            <FormField label="Sig Text" description="Required">
              <Textarea
                value={medication.sigText}
                onChange={({ detail }) => onMedicationChange({ sigText: detail.value })}
                placeholder="Take 1 tablet by mouth daily."
              />
            </FormField>
          </ColumnLayout>

          {useNdc && (
            <ColumnLayout columns={2}>
              <FormField label="NDC" description="Required">
                <Input
                  value={medication.ndc}
                  onChange={({ detail }) => onMedicationChange({ ndc: detail.value })}
                  placeholder="65862045560"
                />
              </FormField>
              <FormField
                label="Quantity Unit of Measure"
                description="Required"
                errorText={uomInvalid ? "Select a unit of measure from the list" : undefined}
              >
                <Autosuggest
                  value={uomInputValue}
                  onChange={({ detail }) => onMedicationChange({ quantityUnitOfMeasure: resolveUom(detail.value) })}
                  onSelect={({ detail }) => onMedicationChange({ quantityUnitOfMeasure: detail.value })}
                  options={uomFilteredOptions}
                  placeholder="Select a unit of measure"
                  empty="No matching units"
                />
              </FormField>
            </ColumnLayout>
          )}

          <ExpandableSection headerText="Advanced medication fields">
            <ColumnLayout columns={2}>
              <FormField label="Drug Description">
                <Input
                  value={medication.drugDescription}
                  onChange={({ detail }) => onMedicationChange({ drugDescription: detail.value })}
                />
              </FormField>
              <FormField label="Strength Value">
                <Input
                  value={medication.strengthValue}
                  onChange={({ detail }) => onMedicationChange({ strengthValue: detail.value })}
                />
              </FormField>
              <FormField label="Drug DB Code">
                <Input
                  value={medication.drugDbCode}
                  onChange={({ detail }) => onMedicationChange({ drugDbCode: detail.value })}
                />
              </FormField>
              <FormField label="Drug DB Qualifier">
                <Input
                  value={medication.drugDbQualifier}
                  onChange={({ detail }) => onMedicationChange({ drugDbQualifier: detail.value })}
                />
              </FormField>
              <FormField label="Quantity Code List Qualifier">
                <Input
                  value={medication.quantityCodeListQualifier}
                  onChange={({ detail }) => onMedicationChange({ quantityCodeListQualifier: detail.value })}
                />
              </FormField>
              <FormField label="Substitutions">
                <Input
                  type="number"
                  value={medication.substitutions}
                  onChange={({ detail }) => onMedicationChange({ substitutions: detail.value })}
                />
              </FormField>
              <FormField label="Written Date">
                <Input
                  value={medication.writtenDate}
                  onChange={({ detail }) => onMedicationChange({ writtenDate: detail.value })}
                  placeholder="YYYYMMDD"
                />
              </FormField>
            </ColumnLayout>
          </ExpandableSection>
        </SpaceBetween>
      </Container>
    );
  }
}

export default NewRxMedicationBlock;
